import { Injectable, signal, WritableSignal } from '@angular/core';
import {
  ForgotPasswordPayload,
  ForgotPasswordResponse,
  LogInPayload,
  LogInResponse,
  RefreshTokenResponse,
  ResetPasswordPayload,
  ResetPasswordResponse,
} from '@core/models/entities/auth.models';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { HttpApi } from './http.api';
import { UserApi } from './user.api';
import { AUTH_RC } from '@core/constants/routes.constants';
import { Router } from '@angular/router';

const feature: string = 'admin';

const ACCESS_TOKEN_KEY: string = 'access_token';

const REFRESH_TOKEN_KEY: string = 'refresh_token';

@Injectable({ providedIn: 'root' })
export class AuthApi {
  readonly isLoading: WritableSignal<boolean> = signal(false);

  readonly prefillEmail: WritableSignal<string> = signal('');

  constructor(
    private readonly httpApi: HttpApi,
    private userApi: UserApi,
    private router: Router
  ) {}

  setLoading(value: boolean): void {
    this.isLoading.set(value);
  }

  setPrefillEmail(value: string): void {
    this.prefillEmail.set(value);
  }

  logIn(payload: LogInPayload): Observable<LogInResponse> {
    return this.httpApi.post<LogInResponse>(`${feature}/login`, payload).pipe(
      tap((response: LogInResponse) => {
        this.userApi.user = response.user;

        this.setAccessToken(response.accessToken);
        this.setRefreshToken(response.refreshToken);
      })
    );
  }

  forgotPassword(payload: ForgotPasswordPayload): Observable<ForgotPasswordResponse> {
    return this.httpApi.post<ForgotPasswordResponse>(`${feature}/forgot-password`, payload, {
      skipSuccessMessage: true,
    });
  }

  resetPassword(payload: ResetPasswordPayload): Observable<ResetPasswordResponse> {
    return this.httpApi.post<ResetPasswordResponse>(`${feature}/reset-password`, payload, { skipSuccessMessage: true });
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  refreshToken(): Observable<RefreshTokenResponse> {
    const refreshToken: string | null = this.getRefreshToken();

    return this.httpApi
      .post<RefreshTokenResponse>(`${feature}/refresh-token`, { refreshToken }, { skipSuccessMessage: true })
      .pipe(
        tap((response: RefreshTokenResponse) => {
          this.setAccessToken(response.accessToken);
          this.setRefreshToken(response.refreshToken);
        })
      );
  }

  logOut(): Observable<boolean> {
    if (!this.isAuthorized()) {
      this.reset();

      of(true);
    }

    return this.httpApi.post<boolean>(`${feature}/logout`, {}, { skipSuccessMessage: true }).pipe(
      map(() => true),
      catchError(err => {
        console.log('Failed to log out', err);
        return of(false);
      }),
      finalize(() => {
        this.reset();
        this.router.navigate([AUTH_RC.root]);
      })
    );
  }

  isAuthorized(): boolean {
    return !!this.getAccessToken();
  }

  private setAccessToken(token: string | null): void {
    if (token) {
      return localStorage.setItem(ACCESS_TOKEN_KEY, token);
    }

    localStorage.removeItem(ACCESS_TOKEN_KEY);
  }

  private setRefreshToken(token: string | null): void {
    if (token) {
      return localStorage.setItem(REFRESH_TOKEN_KEY, token);
    }

    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  private reset(): void {
    this.userApi.user = null;
    this.setAccessToken(null);
    this.setRefreshToken(null);
  }
}
